import {useTranslation} from 'react-i18next';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {Box, Chip, Typography} from '@mui/material';

import {WorkforceProfileSeniorityLevelTile} from './WorkforceProfileSeniorityLevelTile';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {orangeColors, recommendationCallout, semantic} from '../../../../../layouts/theme/designSystem';
import {getSeniorityLevelLabel} from '../../../../../utils/getSeniorityLevelLabel';
import type {SeniorityLevelDelta, WorkforceProfileSeniorityLevelAssessment} from '../../types/WorkforceProfileTypes';

export interface Props {
  assessment: WorkforceProfileSeniorityLevelAssessment;
}

const deltaStyles: Record<
  SeniorityLevelDelta,
  {bgcolor: string; color: string; borderColor: string; Icon: typeof CheckCircleOutlineIcon}
> = {
  at: {
    ...recommendationCallout.fit,
    Icon: CheckCircleOutlineIcon,
  },
  above: {
    bgcolor: semantic.info.bg,
    color: semantic.info.fg,
    borderColor: semantic.info.fg,
    Icon: TrendingUpIcon,
  },
  below: {
    ...recommendationCallout.watch,
    Icon: TrendingDownIcon,
  },
  uncertain: {
    ...recommendationCallout.watch,
    Icon: HelpOutlineIcon,
  },
};

export const WorkforceProfileSeniorityLevelAssessmentCard = ({assessment}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceProfilesPage.detail.seniorityLevelAssessment',
  });
  const {t: tShared} = useTranslation();
  const styles = deltaStyles[assessment.levelDelta];
  const Icon = styles.Icon;
  const assignedLabel = assessment.assignedSeniority
    ? getSeniorityLevelLabel(assessment.assignedSeniority, tShared)
    : null;
  const evidencedLabel = assessment.evidencedLevel ? getSeniorityLevelLabel(assessment.evidencedLevel, tShared) : null;

  return (
    <EmpionPaper sx={{border: `1px solid ${orangeColors[3]}`, borderRadius: '14px', p: 2.5, mb: 2}}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 1.75,
        }}
      >
        <Typography variant='h3' sx={{fontWeight: 600}}>
          {t('title')}
        </Typography>
        <Chip
          icon={<Icon sx={{fontSize: '16px !important'}} />}
          label={t(`levelDelta.${assessment.levelDelta}`)}
          size='small'
          sx={{
            height: 28,
            fontWeight: 600,
            fontSize: 12.5,
            bgcolor: styles.bgcolor,
            color: styles.color,
            border: `1px solid ${styles.borderColor}`,
            '& .MuiChip-icon': {color: 'inherit', ml: 0.75},
          }}
        />
      </Box>

      {(assignedLabel || evidencedLabel) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 1.25,
            mb: 1.75,
          }}
        >
          {assignedLabel && <WorkforceProfileSeniorityLevelTile label={t('assigned')} value={assignedLabel} />}
          {assignedLabel && evidencedLabel && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                px: 0.25,
                flexShrink: 0,
              }}
              aria-hidden
            >
              <Box
                component='span'
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                →
              </Box>
            </Box>
          )}
          {evidencedLabel && <WorkforceProfileSeniorityLevelTile label={t('evidenced')} value={evidencedLabel} />}
        </Box>
      )}

      <Typography variant='body2' color='text.secondary' sx={{lineHeight: 1.65}}>
        {assessment.explanation}
      </Typography>
    </EmpionPaper>
  );
};
